import { useState, useEffect, useCallback } from 'react';
import { adminFetch, apiEndpoints } from '../config/api';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';

const PackageList = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imageUploadFile, setImageUploadFile] = useState(null);
  const [imageUploadTitle, setImageUploadTitle] = useState('');
  const [imageUploadCategory, setImageUploadCategory] = useState('gallery');
  const [imageUploadStatus, setImageUploadStatus] = useState('');
  const [imageUploading, setImageUploading] = useState(false);

  const [newPackage, setNewPackage] = useState({
    name: '',
    description: '',
    park: 'yala',
    block: '',
    packageType: 'private',
    maxCapacity: 7,
    isActive: true,
    availableDates: { startDate: '', endDate: '' },
    jeep: {
      basic: { morning: 0, afternoon: 0, extended: 0, fullDay: 0 },
      luxury: { morning: 0, afternoon: 0, extended: 0, fullDay: 0 },
      superLuxury: { morning: 0, afternoon: 0, extended: 0, fullDay: 0 },
    },
    meals: { breakfast: 1, lunch: 1 },
    mealOptions: { breakfast: [], lunch: [] },
    guide: { driver: 0, driverGuide: 15, separateGuide: 25 },
    tickets: { foreign: 25, local: 8 },
    features: [],
    highlights: [],
    images: [],
    featuredImage: '',
    mealOptionsBreakfastText: '',
    mealOptionsLunchText: '',
    featuresText: '',
    highlightsText: '',
    imagesText: '',
  });

  // Use useCallback to memoize fetchPackages
  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'all' 
        ? apiEndpoints.packages.base 
        : `${apiEndpoints.packages.base}?isActive=${filter === 'active'}`;
      
      const response = await adminFetch(url);
      const data = await response.json();
      
      if (data.success) {
        setPackages(data.packages);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]); // Add filter as dependency

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]); // Now fetchPackages is properly included

  const handleToggleStatus = async (id) => {
    try {
      const response = await adminFetch(
        `${apiEndpoints.packages.base}/${id}/toggle-status`,
        { method: 'PATCH' }
      );
      
      if (response.ok) {
        fetchPackages();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        const response = await adminFetch(
          `${apiEndpoints.packages.base}/${id}`,
          { method: 'DELETE' }
        );
        
        if (response.ok) {
          fetchPackages();
        }
      } catch (error) {
        console.error('Error deleting package:', error);
      }
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (!imageUploadFile || !imageUploadTitle.trim()) {
      setImageUploadStatus('Please select an image and provide a title');
      return;
    }

    setImageUploading(true);
    setImageUploadStatus('');

    try {
      const formData = new FormData();
      formData.append('file', imageUploadFile);
      formData.append('category', imageUploadCategory);
      formData.append('title', imageUploadTitle.trim());

      const response = await adminFetch(apiEndpoints.images.base, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Image upload failed');
      }

      const returnedUrl = result.url || (result.data && result.data.url);
      const urlToUse = returnedUrl
        ? (returnedUrl.startsWith('http') ? returnedUrl : apiEndpoints.images.url(returnedUrl))
        : '';

      if (!urlToUse) {
        throw new Error('Upload succeeded but no URL was returned');
      }

      const existing = newPackage.imagesText ? newPackage.imagesText.split(',').map((i) => i.trim()).filter(Boolean) : [];
      const updatedImages = [...existing, urlToUse];

      setNewPackage({
        ...newPackage,
        imagesText: updatedImages.join(', '),
        featuredImage: newPackage.featuredImage || urlToUse,
      });

      setImageUploadStatus('Image uploaded and added to package');
      setImageUploadFile(null);
      setImageUploadTitle('');
      setImageUploadCategory('gallery');
    } catch (err) {
      console.error('Error uploading image:', err);
      setImageUploadStatus(err.message || 'Image upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  const handleCreatePackage = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const parseList = (text) =>
      text
        .split(',')
        .map((i) => i.trim())
        .filter(Boolean);

    const payload = {
      name: newPackage.name,
      description: newPackage.description,
      park: newPackage.park,
      block: newPackage.block || undefined,
      packageType: 'private',
      isActive: !!newPackage.isActive,
      maxCapacity: Number(newPackage.maxCapacity) || 0,
      availableDates: {
        startDate: newPackage.availableDates.startDate || undefined,
        endDate: newPackage.availableDates.endDate || undefined,
      },
      jeep: {
        basic: {
          morning: Number(newPackage.jeep.basic.morning) || 0,
          afternoon: Number(newPackage.jeep.basic.afternoon) || 0,
          extended: Number(newPackage.jeep.basic.extended) || 0,
          fullDay: Number(newPackage.jeep.basic.fullDay) || 0,
        },
        luxury: {
          morning: Number(newPackage.jeep.luxury.morning) || 0,
          afternoon: Number(newPackage.jeep.luxury.afternoon) || 0,
          extended: Number(newPackage.jeep.luxury.extended) || 0,
          fullDay: Number(newPackage.jeep.luxury.fullDay) || 0,
        },
        superLuxury: {
          morning: Number(newPackage.jeep.superLuxury.morning) || 0,
          afternoon: Number(newPackage.jeep.superLuxury.afternoon) || 0,
          extended: Number(newPackage.jeep.superLuxury.extended) || 0,
          fullDay: Number(newPackage.jeep.superLuxury.fullDay) || 0,
        },
      },
      meals: {
        breakfast: Number(newPackage.meals.breakfast) || 0,
        lunch: Number(newPackage.meals.lunch) || 0,
      },
      mealOptions: {
        breakfast: Array.isArray(newPackage.mealOptions.breakfast) ? newPackage.mealOptions.breakfast : [],
        lunch: Array.isArray(newPackage.mealOptions.lunch) ? newPackage.mealOptions.lunch : [],
      },
      guide: {
        driver: Number(newPackage.guide.driver) || 0,
        driverGuide: Number(newPackage.guide.driverGuide) || 0,
        separateGuide: Number(newPackage.guide.separateGuide) || 0,
      },
      tickets: {
        foreign: Number(newPackage.tickets.foreign) || 0,
        local: Number(newPackage.tickets.local) || 0,
      },
      features: newPackage.featuresText ? parseList(newPackage.featuresText) : [],
      highlights: newPackage.highlightsText ? parseList(newPackage.highlightsText) : [],
      images: newPackage.imagesText ? parseList(newPackage.imagesText) : [],
      featuredImage: newPackage.featuredImage || undefined,
    };
    
    try {
      const url = editingPackageId 
        ? `${apiEndpoints.packages.base}/${editingPackageId}`
        : apiEndpoints.packages.base;
      
      const method = editingPackageId ? 'PUT' : 'POST';
      
      const response = await adminFetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      
      if (data.success) {
        setShowCreateModal(false);
        setEditingPackageId(null);
        setNewPackage({
          name: '',
          description: '',
          park: 'yala',
          block: '',
          packageType: 'private',
          maxCapacity: 7,
          isActive: true,
          availableDates: { startDate: '', endDate: '' },
          jeep: {
            basic: { morning: 120, afternoon: 110, extended: 150, fullDay: 220 },
            luxury: { morning: 160, afternoon: 150, extended: 190, fullDay: 260 },
            superLuxury: { morning: 220, afternoon: 210, extended: 260, fullDay: 330 },
          },
          meals: { breakfast: 1, lunch: 1 },
          mealOptions: { breakfast: [], lunch: [] },
          guide: { driver: 0, driverGuide: 15, separateGuide: 25 },
          tickets: { foreign: 25, local: 8 },
          features: [],
          highlights: [],
          images: [],
          featuredImage: '',
          mealOptionsBreakfastText: '',
          mealOptionsLunchText: '',
          featuresText: '',
          highlightsText: '',
          imagesText: '',
        });
        fetchPackages();
      } else {
        alert(data.message || `Failed to ${editingPackageId ? 'update' : 'create'} package`);
      }
    } catch (error) {
      console.error(`Error ${editingPackageId ? 'updating' : 'creating'} package:`, error);
      alert(`Failed to ${editingPackageId ? 'update' : 'create'} package. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleEditPackage = (pkg) => {
    // Helper function to normalize meal options array
    const normalizeMealOptions = (options) => {
      if (!Array.isArray(options)) return [];
      return options.map(opt => ({
        name: opt.name || '',
        price: opt.price || 0,
        isVegetarian: opt.isVegetarian || false,
        description: opt.description || '',
      }));
    };

    // Helper function to format date for input field (YYYY-MM-DD)
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      } catch {
        return '';
      }
    };

    // Populate form with package data
    setNewPackage({
      name: pkg.name || '',
      description: pkg.description || '',
      park: pkg.park || 'yala',
      block: pkg.block || '',
      packageType: pkg.packageType || 'private',
      maxCapacity: pkg.maxCapacity || 7,
      isActive: pkg.isActive !== undefined ? pkg.isActive : true,
      availableDates: {
        startDate: formatDateForInput(pkg.availableDates?.startDate),
        endDate: formatDateForInput(pkg.availableDates?.endDate),
      },
      jeep: {
        basic: {
          morning: pkg.jeep?.basic?.morning ?? 0,
          afternoon: pkg.jeep?.basic?.afternoon ?? 0,
          extended: pkg.jeep?.basic?.extended ?? 0,
          fullDay: pkg.jeep?.basic?.fullDay ?? 0,
        },
        luxury: {
          morning: pkg.jeep?.luxury?.morning ?? 0,
          afternoon: pkg.jeep?.luxury?.afternoon ?? 0,
          extended: pkg.jeep?.luxury?.extended ?? 0,
          fullDay: pkg.jeep?.luxury?.fullDay ?? 0,
        },
        superLuxury: {
          morning: pkg.jeep?.superLuxury?.morning ?? 0,
          afternoon: pkg.jeep?.superLuxury?.afternoon ?? 0,
          extended: pkg.jeep?.superLuxury?.extended ?? 0,
          fullDay: pkg.jeep?.superLuxury?.fullDay ?? 0,
        },
      },
      meals: {
        breakfast: pkg.meals?.breakfast ?? 1,
        lunch: pkg.meals?.lunch ?? 1,
      },
      mealOptions: {
        breakfast: normalizeMealOptions(pkg.mealOptions?.breakfast),
        lunch: normalizeMealOptions(pkg.mealOptions?.lunch),
      },
      guide: {
        driver: pkg.guide?.driver ?? 0,
        driverGuide: pkg.guide?.driverGuide ?? 15,
        separateGuide: pkg.guide?.separateGuide ?? 25,
      },
      tickets: {
        foreign: pkg.tickets?.foreign ?? 25,
        local: pkg.tickets?.local ?? 8,
      },
      features: Array.isArray(pkg.features) ? pkg.features : [],
      highlights: Array.isArray(pkg.highlights) ? pkg.highlights : [],
      images: Array.isArray(pkg.images) ? pkg.images : [],
      featuredImage: pkg.featuredImage || '',
      mealOptionsBreakfastText: '',
      mealOptionsLunchText: '',
      featuresText: Array.isArray(pkg.features) ? pkg.features.join(', ') : '',
      highlightsText: Array.isArray(pkg.highlights) ? pkg.highlights.join(', ') : '',
      imagesText: Array.isArray(pkg.images) ? pkg.images.join(', ') : '',
    });
    setEditingPackageId(pkg._id);
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingPackageId(null);
    setNewPackage({
      name: '',
      description: '',
      park: 'yala',
      block: '',
      packageType: 'private',
      maxCapacity: 7,
      isActive: true,
      availableDates: { startDate: '', endDate: '' },
      jeep: {
        basic: { morning: 0, afternoon: 0, extended: 0, fullDay: 0 },
        luxury: { morning: 0, afternoon: 0, extended: 0, fullDay: 0 },
        superLuxury: { morning: 0, afternoon: 0, extended: 0, fullDay: 0 },
      },
      meals: { breakfast: 1, lunch: 1 },
      mealOptions: { breakfast: [], lunch: [] },
      guide: { driver: 0, driverGuide: 15, separateGuide: 25 },
      tickets: { foreign: 25, local: 8 },
      features: [],
      highlights: [],
      images: [],
      featuredImage: '',
      mealOptionsBreakfastText: '',
      mealOptionsLunchText: '',
      featuresText: '',
      highlightsText: '',
      imagesText: '',
    });
  };

  if (loading) {
    return (
      <div className="ui-mdc-package-loading">
        <div className="ui-mdc-package-loading-spinner"></div>
        <span>Loading packages...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header Section */}
      <div className="ui-mdc-package-header">
        <h1 className="ui-mdc-package-title">Package Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="ui-mdc-package-button ui-mdc-package-button--primary mt-5"
        >
          + Create New Package
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="ui-mdc-package-filters">
        {['all', 'active', 'inactive'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`ui-mdc-package-filter ${
              filter === f ? 'ui-mdc-package-filter--active' : ''
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Packages Grid */}
      {packages.length > 0 ? (
        <div className="ui-mdc-package-grid mt-5">
          {packages.map((pkg) => (
            <div key={pkg._id} className="ui-mdc-package-item">
              <div className="ui-mdc-package-item-header">
                <div>
                  <h3 className="ui-mdc-package-item-title">{pkg.name}</h3>
                  <p className="ui-mdc-package-item-subtitle">{pkg.park} Park</p>
                </div>
                <span
                  className={`ui-mdc-package-status-badge ${
                    pkg.isActive
                      ? 'ui-mdc-package-status-badge--active'
                      : 'ui-mdc-package-status-badge--inactive'
                  }`}
                >
                  {pkg.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <p className="ui-mdc-package-item-description">
                {pkg.description}
              </p>

              <div className="ui-mdc-package-item-meta">
                <div className="ui-mdc-package-item-meta-item">
                  <span className="ui-mdc-package-item-meta-label">Type</span>
                  <span className="ui-mdc-package-item-meta-value">{pkg.packageType}</span>
                </div>
                <div className="ui-mdc-package-item-meta-item">
                  <span className="ui-mdc-package-item-meta-label">Bookings</span>
                  <span className="ui-mdc-package-item-meta-value">{pkg.totalBookings || 0}</span>
                </div>
              </div>

              <div className="ui-mdc-package-item-actions">
                <button
                  onClick={() => handleEditPackage(pkg)}
                  className="ui-mdc-package-button ui-mdc-package-button--secondary"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleToggleStatus(pkg._id)}
                  className={`ui-mdc-package-button ${
                    pkg.isActive
                      ? 'ui-mdc-package-button--warning'
                      : 'ui-mdc-package-button--primary'
                  }`}
                >
                  {pkg.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDelete(pkg._id)}
                  className="ui-mdc-package-button ui-mdc-package-button--danger"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="ui-mdc-package-empty">
          <p className="ui-mdc-package-empty-title">No packages found</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="ui-mdc-package-button ui-mdc-package-button--primary"
          >
            Create your first package
          </button>
        </div>
      )}

      {/* Create Package Modal */}
      {showCreateModal && (
        <div className="ui-mdc-package-modal">
          <div className="ui-mdc-package-modal-content">
            <div className="ui-mdc-package-modal-header">
              <h3 className="ui-mdc-package-modal-title">
                {editingPackageId ? 'Edit Package' : 'Create New Package'}
              </h3>
              <button
                type="button"
                onClick={handleCloseModal}
                className="ui-mdc-package-modal-close"
                aria-label="Close modal"
              >
                <FiX />
              </button>
            </div>
            <form onSubmit={handleCreatePackage} className="ui-mdc-package-modal-form">
              <div className="ui-mdc-package-form-group">
                <label className="ui-mdc-package-form-label">Package Name *</label>
                <input
                  type="text"
                  required
                  value={newPackage.name}
                  onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                  className="ui-mdc-package-form-input"
                  placeholder="e.g., Premium Yala Safari"
                />
              </div>

              <div className="ui-mdc-package-form-group">
                <label className="ui-mdc-package-form-label">Description *</label>
                <textarea
                  required
                  rows="4"
                  value={newPackage.description}
                  onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })}
                  className="ui-mdc-package-form-input ui-mdc-package-form-textarea"
                  placeholder="Describe the safari experience..."
                />
              </div>

              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Park *</label>
                  <select
                    required
                    value={newPackage.park}
                    onChange={(e) => setNewPackage({ ...newPackage, park: e.target.value, block: '' })}
                    className="ui-mdc-package-form-input"
                 
                  >
                    <option value="yala" style={{ color: '#000000' }}>Yala National Park</option>
                    <option value="bundala" style={{ color: '#000000' }}>Bundala National Park</option>
                    <option value="udawalawe" style={{ color: '#000000' }}>Udawalawe National Park</option>
                    <option value="Lunugamwehera" style={{ color: '#000000' }}>Lunugamwehera National Park</option>
                  </select>
                </div>

                {newPackage.park === 'yala' && (
                  <div className="ui-mdc-package-form-group">
                    <label className="ui-mdc-package-form-label">Block</label>
                    <select
                      value={newPackage.block}
                      onChange={(e) => setNewPackage({ ...newPackage, block: e.target.value })}
                      className="ui-mdc-package-form-input"
                     
                    >
                      <option value="" style={{ color: '#000000' }}>All Blocks</option>
                      <option value="blockI" style={{ color: '#000000' }}>Block I</option>
                      <option value="blockII" style={{ color: '#000000' }}>Block II</option>
                      <option value="blockIIIIV" style={{ color: '#000000' }}>Block III & IV</option>
                      <option value="blockV" style={{ color: '#000000' }}>Block V</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Package Type *</label>
                  <select
                    required
                    value={newPackage.packageType}
                    onChange={(e) => setNewPackage({ ...newPackage, packageType: e.target.value })}
                    className="ui-mdc-package-form-input"
                    disabled
                    style={{ color: '#000000' }}
                  >
                    <option value="private" style={{ color: '#000000' }}>Private Only</option>
                  </select>
                </div>

                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Max Capacity</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={newPackage.maxCapacity}
                    onChange={(e) => setNewPackage({ ...newPackage, maxCapacity: Number(e.target.value) })}
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>

              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Available From</label>
                  <input
                    type="date"
                    value={newPackage.availableDates.startDate}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        availableDates: { ...newPackage.availableDates, startDate: e.target.value },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Available To</label>
                  <input
                    type="date"
                    value={newPackage.availableDates.endDate}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        availableDates: { ...newPackage.availableDates, endDate: e.target.value },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>

              {/* Jeep Pricing */}
              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Basic - Morning</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.basic.morning}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          basic: { ...newPackage.jeep.basic, morning: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Basic - Afternoon</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.basic.afternoon}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          basic: { ...newPackage.jeep.basic, afternoon: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>
              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Basic - Extended</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.basic.extended}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          basic: { ...newPackage.jeep.basic, extended: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Basic - Full Day</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.basic.fullDay}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          basic: { ...newPackage.jeep.basic, fullDay: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>

              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Luxury - Morning</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.luxury.morning}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          luxury: { ...newPackage.jeep.luxury, morning: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Luxury - Afternoon</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.luxury.afternoon}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          luxury: { ...newPackage.jeep.luxury, afternoon: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>
              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Luxury - Extended</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.luxury.extended}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          luxury: { ...newPackage.jeep.luxury, extended: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Luxury - Full Day</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.luxury.fullDay}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          luxury: { ...newPackage.jeep.luxury, fullDay: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>

              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Super Luxury - Morning</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.superLuxury.morning}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          superLuxury: { ...newPackage.jeep.superLuxury, morning: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Super Luxury - Afternoon</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.superLuxury.afternoon}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          superLuxury: { ...newPackage.jeep.superLuxury, afternoon: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>
              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Super Luxury - Extended</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.superLuxury.extended}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          superLuxury: { ...newPackage.jeep.superLuxury, extended: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Super Luxury - Full Day</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.jeep.superLuxury.fullDay}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        jeep: {
                          ...newPackage.jeep,
                          superLuxury: { ...newPackage.jeep.superLuxury, fullDay: Number(e.target.value) },
                        },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>

              {/* Meals */}
              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Breakfast Price</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.meals.breakfast}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        meals: { ...newPackage.meals, breakfast: Number(e.target.value) },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Lunch Price</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.meals.lunch}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        meals: { ...newPackage.meals, lunch: Number(e.target.value) },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>

              {/* Meal Options - Breakfast */}
              <div className="ui-mdc-package-form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label className="ui-mdc-package-form-label">
                    Meal Options - Breakfast
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setNewPackage({
                        ...newPackage,
                        mealOptions: {
                          ...newPackage.mealOptions,
                          breakfast: [
                            ...(newPackage.mealOptions.breakfast || []),
                            { name: '', price: 0, isVegetarian: false, description: '' }
                          ]
                        }
                      });
                    }}
                    style={{
                      padding: '0.4rem 0.8rem',
                      backgroundColor: '#c4915f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontSize: '0.85rem',
                    }}
                  >
                    <FiPlus size={14} />
                    Add Option
                  </button>
                </div>
                {(!newPackage.mealOptions.breakfast || newPackage.mealOptions.breakfast.length === 0) ? (
                  <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem', fontStyle: 'italic' }}>
                    No breakfast options added. Click "Add Option" to add one.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {newPackage.mealOptions.breakfast.map((option, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '1rem',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '0.5rem',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                          <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600, fontSize: '0.9rem' }}>
                            Option {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = [...(newPackage.mealOptions.breakfast || [])];
                              updated.splice(index, 1);
                              setNewPackage({
                                ...newPackage,
                                mealOptions: {
                                  ...newPackage.mealOptions,
                                  breakfast: updated
                                }
                              });
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: 'rgba(239, 68, 68, 0.8)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontSize: '0.75rem',
                            }}
                          >
                            <FiTrash2 size={12} />
                            Remove
                          </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                              Name *
                            </label>
                            <input
                              type="text"
                              required
                              value={option.name || ''}
                              onChange={(e) => {
                                const updated = [...(newPackage.mealOptions.breakfast || [])];
                                updated[index] = { ...updated[index], name: e.target.value };
                                setNewPackage({
                                  ...newPackage,
                                  mealOptions: {
                                    ...newPackage.mealOptions,
                                    breakfast: updated
                                  }
                                });
                              }}
                              className="ui-mdc-package-form-input"
                              placeholder="e.g., Sandwich"
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                              Price *
                            </label>
                            <input
                              type="number"
                              required
                              min="0"
                              step="0.01"
                              value={option.price || 0}
                              onChange={(e) => {
                                const updated = [...(newPackage.mealOptions.breakfast || [])];
                                updated[index] = { ...updated[index], price: Number(e.target.value) || 0 };
                                setNewPackage({
                                  ...newPackage,
                                  mealOptions: {
                                    ...newPackage.mealOptions,
                                    breakfast: updated
                                  }
                                });
                              }}
                              className="ui-mdc-package-form-input"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.25rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                            Description
                          </label>
                          <input
                            type="text"
                            value={option.description || ''}
                            onChange={(e) => {
                              const updated = [...(newPackage.mealOptions.breakfast || [])];
                              updated[index] = { ...updated[index], description: e.target.value };
                              setNewPackage({
                                ...newPackage,
                                mealOptions: {
                                  ...newPackage.mealOptions,
                                  breakfast: updated
                                }
                              });
                            }}
                            className="ui-mdc-package-form-input"
                            placeholder="e.g., Veg sandwich with fresh vegetables"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={option.isVegetarian || false}
                              onChange={(e) => {
                                const updated = [...(newPackage.mealOptions.breakfast || [])];
                                updated[index] = { ...updated[index], isVegetarian: e.target.checked };
                                setNewPackage({
                                  ...newPackage,
                                  mealOptions: {
                                    ...newPackage.mealOptions,
                                    breakfast: updated
                                  }
                                });
                              }}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                              Vegetarian
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Meal Options - Lunch */}
              <div className="ui-mdc-package-form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label className="ui-mdc-package-form-label">
                    Meal Options - Lunch
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setNewPackage({
                        ...newPackage,
                        mealOptions: {
                          ...newPackage.mealOptions,
                          lunch: [
                            ...(newPackage.mealOptions.lunch || []),
                            { name: '', price: 0, isVegetarian: false, description: '' }
                          ]
                        }
                      });
                    }}
                    style={{
                      padding: '0.4rem 0.8rem',
                      backgroundColor: '#c4915f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontSize: '0.85rem',
                    }}
                  >
                    <FiPlus size={14} />
                    Add Option
                  </button>
                </div>
                {(!newPackage.mealOptions.lunch || newPackage.mealOptions.lunch.length === 0) ? (
                  <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem', fontStyle: 'italic' }}>
                    No lunch options added. Click "Add Option" to add one.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {newPackage.mealOptions.lunch.map((option, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '1rem',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '0.5rem',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                          <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600, fontSize: '0.9rem' }}>
                            Option {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = [...(newPackage.mealOptions.lunch || [])];
                              updated.splice(index, 1);
                              setNewPackage({
                                ...newPackage,
                                mealOptions: {
                                  ...newPackage.mealOptions,
                                  lunch: updated
                                }
                              });
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: 'rgba(239, 68, 68, 0.8)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontSize: '0.75rem',
                            }}
                          >
                            <FiTrash2 size={12} />
                            Remove
                          </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                              Name *
                            </label>
                            <input
                              type="text"
                              required
                              value={option.name || ''}
                              onChange={(e) => {
                                const updated = [...(newPackage.mealOptions.lunch || [])];
                                updated[index] = { ...updated[index], name: e.target.value };
                                setNewPackage({
                                  ...newPackage,
                                  mealOptions: {
                                    ...newPackage.mealOptions,
                                    lunch: updated
                                  }
                                });
                              }}
                              className="ui-mdc-package-form-input"
                              placeholder="e.g., Rice & Curry"
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '0.25rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                              Price *
                            </label>
                            <input
                              type="number"
                              required
                              min="0"
                              step="0.01"
                              value={option.price || 0}
                              onChange={(e) => {
                                const updated = [...(newPackage.mealOptions.lunch || [])];
                                updated[index] = { ...updated[index], price: Number(e.target.value) || 0 };
                                setNewPackage({
                                  ...newPackage,
                                  mealOptions: {
                                    ...newPackage.mealOptions,
                                    lunch: updated
                                  }
                                });
                              }}
                              className="ui-mdc-package-form-input"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.25rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                            Description
                          </label>
                          <input
                            type="text"
                            value={option.description || ''}
                            onChange={(e) => {
                              const updated = [...(newPackage.mealOptions.lunch || [])];
                              updated[index] = { ...updated[index], description: e.target.value };
                              setNewPackage({
                                ...newPackage,
                                mealOptions: {
                                  ...newPackage.mealOptions,
                                  lunch: updated
                                }
                              });
                            }}
                            className="ui-mdc-package-form-input"
                            placeholder="e.g., Traditional Sri Lankan rice and curry"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={option.isVegetarian || false}
                              onChange={(e) => {
                                const updated = [...(newPackage.mealOptions.lunch || [])];
                                updated[index] = { ...updated[index], isVegetarian: e.target.checked };
                                setNewPackage({
                                  ...newPackage,
                                  mealOptions: {
                                    ...newPackage.mealOptions,
                                    lunch: updated
                                  }
                                });
                              }}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>
                              Vegetarian
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Guide & Tickets */}
              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Driver (Guide)</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.guide.driver}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        guide: { ...newPackage.guide, driver: Number(e.target.value) },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Driver + Guide</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.guide.driverGuide}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        guide: { ...newPackage.guide, driverGuide: Number(e.target.value) },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>
              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Separate Guide</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.guide.separateGuide}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        guide: { ...newPackage.guide, separateGuide: Number(e.target.value) },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Tickets - Foreign</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.tickets.foreign}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        tickets: { ...newPackage.tickets, foreign: Number(e.target.value) },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>
              <div className="ui-mdc-package-form-row">
                <div className="ui-mdc-package-form-group">
                  <label className="ui-mdc-package-form-label">Tickets - Local</label>
                  <input
                    type="number"
                    min="0"
                    value={newPackage.tickets.local}
                    onChange={(e) =>
                      setNewPackage({
                        ...newPackage,
                        tickets: { ...newPackage.tickets, local: Number(e.target.value) },
                      })
                    }
                    className="ui-mdc-package-form-input"
                  />
                </div>
              </div>

              {/* Media & metadata */}
              <div className="ui-mdc-package-form-group">
                <label className="ui-mdc-package-form-label">Upload Image (saves URL)</label>
                <div className="ui-mdc-package-form-row">
                  <div className="ui-mdc-package-form-group" style={{ flex: 1 }}>
                    <label className="ui-mdc-package-form-label">Title</label>
                    <input
                      type="text"
                      value={imageUploadTitle}
                      onChange={(e) => setImageUploadTitle(e.target.value)}
                      className="ui-mdc-package-form-input"
                      placeholder="e.g., Yala wildlife"
                    />
                  </div>
                  <div className="ui-mdc-package-form-group" style={{ flex: 1 }}>
                    <label className="ui-mdc-package-form-label">Category</label>
                    <select
                      value={imageUploadCategory}
                      onChange={(e) => setImageUploadCategory(e.target.value)}
                      className="ui-mdc-package-form-input"
                      style={{ color: '#000000' }}
                    >
                      <option value="gallery" style={{ color: '#000000' }}>Gallery</option>
                      <option value="wildlife" style={{ color: '#000000' }}>Wildlife</option>
                      <option value="jeep" style={{ color: '#000000' }}>Jeep</option>
                      <option value="hotel" style={{ color: '#000000' }}>Hotel</option>
                    </select>
                  </div>
                </div>
                <div className="ui-mdc-package-form-row" style={{ alignItems: 'flex-end' }}>
                  <div className="ui-mdc-package-form-group" style={{ flex: 1 }}>
                    <label className="ui-mdc-package-form-label">Image File</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setImageUploadFile(e.target.files[0] || null)}
                      className="ui-mdc-package-form-input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleImageUpload}
                    className="ui-mdc-package-button ui-mdc-package-button--secondary"
                    disabled={imageUploading}
                    style={{ marginLeft: '12px', marginBottom: '4px' }}
                  >
                    {imageUploading ? 'Uploading...' : 'Upload & Add'}
                  </button>
                </div>
                {imageUploadStatus && (
                  <p className="ui-mdc-booking-error" style={{ marginTop: '8px' }}>
                    {imageUploadStatus}
                  </p>
                )}
              </div>

              <div className="ui-mdc-package-form-group">
                <label className="ui-mdc-package-form-label">Images (comma separated URLs)</label>
                <input
                  type="text"
                  value={newPackage.imagesText}
                  onChange={(e) => setNewPackage({ ...newPackage, imagesText: e.target.value })}
                  className="ui-mdc-package-form-input"
                  placeholder="https://... , https://..."
                />
              </div>
              <div className="ui-mdc-package-form-group">
                <label className="ui-mdc-package-form-label">Featured Image URL</label>
                <input
                  type="text"
                  value={newPackage.featuredImage}
                  onChange={(e) => setNewPackage({ ...newPackage, featuredImage: e.target.value })}
                  className="ui-mdc-package-form-input"
                  placeholder="https://cover-image..."
                />
              </div>

              <div className="ui-mdc-package-form-group">
                <label className="ui-mdc-package-form-label">Features (comma separated)</label>
                <input
                  type="text"
                  value={newPackage.featuresText}
                  onChange={(e) => setNewPackage({ ...newPackage, featuresText: e.target.value })}
                  className="ui-mdc-package-form-input"
                  placeholder="Luxury jeep, Hotel pickup, Bottled water"
                />
              </div>
              <div className="ui-mdc-package-form-group">
                <label className="ui-mdc-package-form-label">Highlights (comma separated)</label>
                <input
                  type="text"
                  value={newPackage.highlightsText}
                  onChange={(e) => setNewPackage({ ...newPackage, highlightsText: e.target.value })}
                  className="ui-mdc-package-form-input"
                  placeholder="Leopard spotting, Bird watching"
                />
              </div>

              <div className="ui-mdc-package-form-group">
                <label className="ui-mdc-package-form-checkbox">
                  <input
                    type="checkbox"
                    checked={newPackage.isActive}
                    onChange={(e) => setNewPackage({ ...newPackage, isActive: e.target.checked })}
                    className="ui-mdc-package-checkbox-input"
                  />
                  <span className="ui-mdc-package-checkbox-label">Active Package</span>
                </label>
              </div>

              <div className="ui-mdc-package-modal-actions">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="ui-mdc-package-button ui-mdc-package-button--secondary"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ui-mdc-package-button ui-mdc-package-button--primary"
                  disabled={saving}
                >
                  {saving 
                    ? (editingPackageId ? 'Updating...' : 'Creating...') 
                    : (editingPackageId ? 'Update Package' : 'Create Package')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageList;
